import React, { Fragment, useState } from 'react';
import styles from './SearchFilter.module.scss';
import {
  SFAutocomplete,
  SFAutocompleteRefHandler,
  SFIcon,
  SFIconButton,
  SFMenuOption,
  SFSwitch
} from 'sfui';
import { Divider } from '../../../../Components/Divider/Divider';
import {
  addNewSearchText,
  getPreviousSearches
} from '../../../../Services/QuickSearchService';
import { isEmpty } from 'lodash';

interface FilterListboxProps {
  isEmptyList: boolean;
  searchText: string;
  advancedFilters: boolean;
  onSearchText: () => void;
  onAdvancedFiltersSwitchChange: (checked: boolean) => void;
}

const ListboxComponent = React.forwardRef<HTMLDivElement>(
  function ListboxComponent({ children, ...props }, ref) {
    const {
      isEmptyList,
      searchText,
      advancedFilters,
      onSearchText,
      onAdvancedFiltersSwitchChange,
      ...others
    } = props as FilterListboxProps;

    return (
      <div ref={ref} {...others} className={styles.searchListbox}>
        {searchText && searchText.length > 1 && (
          <Fragment>
            <div
              className={styles.searchHeader}
              aria-hidden="true"
              onClick={() => onSearchText()}
            >
              <SFIcon
                icon="Search"
                size={22}
                className={styles.searchHeadeIcon}
              />{' '}
              <div className={styles.searchHeaderText}>
                All search results for{' '}
                <span>
                  <strong>"{searchText}"</strong>
                </span>
              </div>
            </div>
            <Divider className={styles.divider} />
          </Fragment>
        )}
        {!isEmptyList && (
          <Fragment>
            {children}
            <Divider className={styles.divider} />
          </Fragment>
        )}
        <div className={styles.searchFooter}>
          <SFSwitch
            label="Advanced Search"
            size="small"
            checked={advancedFilters}
            onChange={(_e, checked: boolean) =>
              onAdvancedFiltersSwitchChange(checked)
            }
          />
        </div>
      </div>
    );
  }
);

export interface SearchFilterProps {
  className?: string;
  placeholder?: string;
  label?: string | undefined;
  disable?: boolean;
  previousSearchText: string;
  isAdvancedFiltersActive: boolean;
  onAdvancedFiltersSwitchChange: (active: boolean) => void;
  onSearchText: (searchText: string) => void;
  onSelect: (search: string) => void;
  onClearSearchText: () => void;
}

export const SearchFilter = React.forwardRef<
  SFAutocompleteRefHandler,
  SearchFilterProps
>((props, ref) => {
  const [value, setValue] = useState<string>(props.previousSearchText || '');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<SFMenuOption[] | undefined>([]);
  const [isEmptyPreviousSearches, setIsEmptyPreviousSearches] =
    useState<boolean>(false);
  const [previousSearches, setPreviousSearches] = useState<string[]>(
    getPreviousSearches() || []
  );

  const onChange = (value: string | SFMenuOption) => {
    if (!isOpen) setIsOpen(true); //Fix for onBlur when user selects the previous value
    if (typeof value === 'string') {
      setValue(value);
    } else {
      const val: string = (value as SFMenuOption).value;
      setValue(val);
      props.onSelect(val);
      closePupup();
    }
  };

  const handleSwitchChange = (active: boolean) => {
    props.onAdvancedFiltersSwitchChange(active);
    closePupup();
  };

  const handleSearchValue = () => {
    if (value && value.length > 1) {
      props.onSearchText(value);
      addNewSearchText(value);
      setPreviousSearches(getPreviousSearches);
      closePupup();
    }
  };

  const onBlur = () => {
    closePupup();
    setValue(props.previousSearchText);
  };

  const onClearSearch = () => {
    setValue('');
    closePupup();
    props.onClearSearchText();
  };

  const openPopup = () => {
    setIsOpen(true);
  };

  const closePupup = () => {
    setIsOpen(false);
  };

  React.useEffect(() => {
    const options: SFMenuOption[] | undefined = previousSearches?.map(
      (text: string) => ({
        label: text,
        value: text
      })
    );

    //Workaround for display Listbox for empty previous searches
    //It is not visible for users
    if (!options || options.length === 0) {
      setOptions([{ label: 'Empty', value: 'empty' }]);
      setIsEmptyPreviousSearches(true);
    } else {
      setOptions(options);
      setIsEmptyPreviousSearches(false);
    }
  }, [previousSearches]);

  return (
    <div className={styles.searchFilter}>
      <SFAutocomplete
        ref={ref}
        className={`${styles.searchFilterText} ${props.className || ''}`}
        label={props.label}
        disabled={props.disable}
        popupIconType="search"
        disableClearable
        clearOnBlur
        autoHighlight={false}
        freeSolo={true}
        onFocus={openPopup}
        open={isOpen}
        options={options || []}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSearchValue();
          }
        }}
        aria-label="Search"
        placeholder={props.placeholder || 'Search'}
        ListboxComponent={
          ListboxComponent as React.ComponentType<
            React.HTMLAttributes<HTMLElement>
          >
        }
        ListboxProps={
          {
            isEmptyList: isEmptyPreviousSearches,
            searchText: value,
            onSearchText: () => handleSearchValue(),
            advancedFilters: props.isAdvancedFiltersActive,
            onAdvancedFiltersSwitchChange: (active: boolean) =>
              handleSwitchChange(active)
          } as FilterListboxProps
        }
        disablePortal={true}
      />
      <div className={styles.searchIcons}>
        {!isEmpty(value) && (
          <SFIconButton
            sfSize="small"
            sfIcon="Close"
            disabled={props.disable}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              onClearSearch();
            }}
          ></SFIconButton>
        )}
        <SFIcon
          className={styles.searchIcon}
          size={'16px'}
          icon="Search"
        ></SFIcon>
      </div>
    </div>
  );
});
